import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loading from "../../../common/components/ui/loading/Loading";
import { formatDateAndTime } from "../../../common/helpers/date.helpers";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { getCompanyId } from "../../../common/services/company.api";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import {
  getVehicles,
  makeVehicleAvailableForRent,
} from "../../../common/services/vehicles.api";
import ModalCreateVehicle from "./modals/CreateVehicle";
import ModalEditVehicle from "./modals/EditVehicle";
import { IVehicle, VEHICLE_STATUS } from "../../../types/vehicle.type";
import ModalPreparationVehicle from "./modals/ModalPreparationVehicle";
import VehiclePreparationStatus from "./VehiclePreparationStatus";
import ModalChangeStatusVehicle from "./modals/ChangeStatusVehicle";
import { ModalFinePayment } from "./modals/FinePayment";
import { AuthContext } from "../../../common/components/auth/authContext/AuthContext";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import ModalChangeStatus from "./modals/ModalChangeStatus";
import { useCompanyStore } from "@/common/states/store/Company";
import ModalConfirmStatus from "@/modules/purchase-order/components/modals/ModalConfirmStatus";

function formatVehicleList(vehicles: IVehicle[]) {
  return vehicles.map((vehicle) => {
    const updatedVehiclePreparing = {
      ...vehicle.vehiclePreparing,
      insuranceStatus: vehicle.insuranceStatus === "protected",
    };

    return {
      ...vehicle,
      vehiclePreparing: updatedVehiclePreparing,
    };
  });
}

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [VEHICLE_STATUS.AVAILABLE]: "Disponível",
  [VEHICLE_STATUS.MISSING_DATA]: "Dados incompletos",
  [VEHICLE_STATUS.RENTED]: "Locado",
  [VEHICLE_STATUS.INACTIVE]: "Inativo",
  [VEHICLE_STATUS.WAITING_PAYMENT]: "Aguardando pagamento",
  [VEHICLE_STATUS.SELLED]: "Veículo vendido",
  [VEHICLE_STATUS.UNDER_PREPARATION]: "Em preparação",
  [VEHICLE_STATUS.UNDER_REVIEW]: "Revisão Programada",
  [VEHICLE_STATUS.IN_REPAIR]: "Em manutenção",
  [VEHICLE_STATUS.IN_REVISION]: "Reparo estético",
  [VEHICLE_STATUS.INSURANCE]: "Abertura de acionamento",
  [VEHICLE_STATUS.TRIGGER_OPENING]: "Proteção acionada",
  [VEHICLE_STATUS.IMPOUNDED]: "Apreendido",
  [VEHICLE_STATUS.THIRD_PARTY_INSURANCE]: "Seguro de Terceiros",
  [VEHICLE_STATUS.PREPARATION_FOR_SALE]: "Motos para venda",
  [VEHICLE_STATUS.IN_TRANSIT]: "Em trânsito",
  [VEHICLE_STATUS.THEFT_ROBBERY]: "Furto/Roubo",
  [VEHICLE_STATUS.MISAPPROPRIATION]: "Apropriação indébita",
  [VEHICLE_STATUS.VEHICLE_ON_RESERVED]: "Moto reserva",
  [VEHICLE_STATUS.REIMBURSEMENT]: "Aguard. Ressarcimento",
  [VEHICLE_STATUS.TRIAGE]: "Triagem",
  [VEHICLE_STATUS.IN_READY_RESPONSE]: "Em pronta resposta",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabelsVehicle(status: VEHICLE_STATUS) {
  return STATUS_LABELS[status] || status;
}

const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  companyId: "Cidade",
  companyName: "Parceiro",
  licensePlate: "Placa",
  model: "Modelo",
  status: "Status",
  updatedAt: "Atualizado em",
  createdAt: "Criado em",
  preparation: "Preparação",
  odometerLocavibe: "Hodômetro",
  isEligibleSuaVibe: "Sua vibe",
};

function TableVehicles() {
  useContext(AuthContext);
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const { auth } = useContext(AuthContext);
  const isAdmin = !!auth?.user?.roles?.includes("admin");
  const isManager = !!auth?.user?.roles?.includes("manager");
  const isAdminOrManager = isAdmin || isManager;
  const { notifyError, notifySuccess } = useToastContext();
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [vehicles, setVehicles] = useState<IVehicle[]>([]);
  const [page, setPage] = useState<number>(1);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "",
  });

  const company = useCompanyStore();

  useEffect(() => {
    if (status !== null) {
      setFilters((prev) => ({
        ...prev,
        status: [status],
      }));
    }
  }, [status]);

  useEffect(() => {
    async function loadVehicles(filters) {
      setIsLoading(true);
      setVehicles([]);

      const result = await getVehicles({
        ...filters,
        page,
        // partnerName: partnerName.map(option => option.label).join(','),
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setVehicles(
        result.data.map((vehicle) => ({
          ...vehicle,
          companyName: vehicle.partnerInfo?.companyName,
        }))
      );

      setIsLoading(false);
    }

    loadVehicles(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, companyId, company]);

  const actions = [
    {
      label: "editar",
      action: (data) =>
        setAction({ eventName: "edit", data: (data as IVehicle)._id }),
      permission: "edit:vehicles",
    },
    {
      label: "Preparação",
      action: (data) => setAction({ eventName: "preparation", data }),
      disabled: (data) => {
        const { status } = data as IVehicle;
        const companyId = getCompanyId();
        return (
          companyId === "*" ||
          (status !== VEHICLE_STATUS.UNDER_PREPARATION &&
            status !== VEHICLE_STATUS.IN_REVISION)
        );
      },
      permission: "edit:vehicles",
    },
    {
      label: "Trocar status",
      action: (data) => {
        setAction({ eventName: "changeStatus", data });
      },
      // disabled: (data) => {
      //   const vehicle = data as IVehicle;
      //   const prep = vehicle.vehiclePreparing;
      //   if (!prep) {
      //     return true;
      //   }
      //   const requiredFields = [
      //     prep.paintingProtection,
      //     prep.tracker,
      //     prep.cellphoneSupport,
      //     prep.phoneChargerSupport,
      //     prep.engineProtection,
      //     prep.trackerValidation,
      //     prep.crlvDocument,
      //     prep.nfDocument,
      //   ];
      //   const isFullyPrepared = requiredFields.every(Boolean);
      //   return !isFullyPrepared;
      // },
      permission: "status:vehicles",
    },
    {
      label: "Gerar multa",
      action: (data) => setAction({ eventName: "fine", data: data }),
      permission: "reserve:renters",
    },

    {
      label: "Alterar Situação",
      visible: (data) =>
        [VEHICLE_STATUS.AVAILABLE].includes((data as IVehicle).status),
      action: (data) => setAction({ eventName: "changeSituation", data }),
      permission: "edit:vehicles",
    },
    {
      label: "Tornar Disponível",
      visible: (data) =>
        isAdminOrManager && (data as IVehicle).availableForRent === false,
      action: (data) => {
        setAction({ eventName: "makeAvailable", data });
      },
      permission: "edit:vehicles",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  const onVehicleCreated = (vehicleId: string) => {
    handleSearch(filters);
    setAction({
      eventName: "edit",
      data: vehicleId,
    });
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Veículos cadastrados"
          placeholder="Digite a placa, o chassi, o hodômetro ou o modelo."
          statusOptions={STATUS_LABELS}
          newItemLabel="Novo veículo"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="vehicles"
          tableData={vehicles as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          subtitles={{
            preparation: (
              <>
                <span
                  role="PP"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="PPF"
                >
                  PP
                </span>
                <span
                  role="RT"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Rastreador"
                >
                  RT
                </span>
                <span
                  role="SC"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Suporte Celular"
                >
                  SC
                </span>
                <span
                  role="CC"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Carregador Celular"
                >
                  CC
                </span>
                <span
                  role="PC"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Protetor Carenagem"
                >
                  PC
                </span>
                <span
                  role="VR"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Verificação Rastreador"
                >
                  VR
                </span>
                <span
                  role="CV"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="CRLV"
                >
                  CV
                </span>
                <span
                  role="NF"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Nota Fiscal"
                >
                  NF
                </span>
                <span
                  role="PV"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Proteção Veicular"
                >
                  PV
                </span>
                <span
                  role="CR"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Chave Reserva"
                >
                  CR
                </span>
              </>
            ) as unknown as string,
          }}
          data={
            formatVehicleList(vehicles) as unknown as Record<string, unknown>[]
          }
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId);
              },
              licensePlate: (licensePlate, vehicle) => (
                <a
                  href={`/veiculos/${(vehicle as IVehicle).licensePlate}`}
                  onClick={(e) => {
                    e.preventDefault(); // Para evitar o comportamento padrão do link
                    navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`); // Navegação na mesma página
                  }}
                  target="_blank" // Abre em uma nova aba
                  rel="noopener noreferrer" // Melhoria de segurança
                >
                  {licensePlate as string}
                </a>
              ),
              status: getStatusLabelsVehicle,
              odometerLocavibe: (odometerLocavibe) => odometerLocavibe || "-",
              isEligibleSuaVibe: (_, vehicle) => {
                if (!vehicle.odometerLocavibe) {
                  return "-";
                }
                const odometerLocavibe = parseFloat(vehicle.odometerLocavibe);
                if (odometerLocavibe >= 0 && odometerLocavibe <= 9999) {
                  return "SV0K";
                } else if (
                  odometerLocavibe >= 10000 &&
                  odometerLocavibe <= 59999
                ) {
                  return "SV10K";
                } else {
                  return "SV60K";
                }
              },
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              preparation: (_, vehicle) => (
                <VehiclePreparationStatus
                  vehiclePreparing={vehicle.vehiclePreparing}
                />
              ),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="vehicles"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("create") && (
          <ModalCreateVehicle
            onClose={cleanActionData}
            onSave={onVehicleCreated}
          />
        )}
        {isAction("makeAvailable") && (
          <ModalConfirmStatus
            open={true}
            title="Confirmar Disponibilidade"
            description="Tem certeza que deseja tornar este veículo disponível para locação?"
            confirmLabel="Sim, Tornar Disponível"
            cancelLabel="Cancelar"
            confirmType="approve"
            onCancel={cleanActionData}
            onConfirm={async () => {
              const vehicle = action.data as IVehicle;
              try {
                const result = await makeVehicleAvailableForRent(vehicle._id);
                if (result.ok) {
                  notifySuccess("Veículo agora está disponível para locação!");
                  cleanActionData();
                } else {
                  notifyError(result.message || "Erro ao atualizar veículo.");
                }
              } catch (error) {
                notifyError("Ocorreu um erro na comunicação com o servidor.");
              }
            }}
          />
        )}
        {isAction("edit") && (
          <ModalEditVehicle
            onClose={cleanActionData}
            onSave={cleanActionData}
            vehicleId={action.data as string}
          />
        )}
        {isAction("preparation") && (
          <ModalPreparationVehicle
            vehicle={action.data as IVehicle}
            onClose={cleanActionData}
          />
        )}
        {isAction("changeStatus") && (
          <ModalChangeStatusVehicle
            vehicle={action.data as IVehicle}
            onClose={cleanActionData}
          />
        )}
        {isAction("fine") && (
          <ModalFinePayment
            vehicle={action.data as { _id: string; licensePlate: string }}
            onClose={cleanActionData}
          />
        )}

        {isAction("changeSituation") && (
          <ModalChangeStatus
            vehicleId={(action.data as IVehicle)._id}
            isOpen={true}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableVehicles;
